import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Head from 'next/head';

import { PrimaryButton, SecondaryButton } from 'components/uiLibrary/LinkButton';
import Loader from 'components/uiLibrary/Loader';
import Typography from 'components/uiLibrary/Typography';

import Login from 'components/Globals/Accounts/Login';
import { EVENT_TYPES, EVENTS_ENDPOINTS } from 'components/Globals/Analytics/constants';
import SEOComponent from 'components/NextSeo';

import { sentryCaptureMessage } from 'utils/globals/sentry';
import useAppContext from 'utils/hooks/useAppContext';
import { useUserData } from 'utils/hooks/useAuthenticatedUser';
import useMarkerIO from 'utils/hooks/useMarkerIO';

import { useTranslation } from 'src/i18n';

import { TP, RENDERING_STATUSES, VISITOR_TYPES, SUSPICIOUS_STATES } from 'operabase-router/constants';

import { blitzerAPI } from '../Analytics/service';
import CommonTrans from '../CommonTrans';
import ErrorContent from '../Layout/ErrorContent';

import classes from './SuspiciousUser.module.scss';

const SUSPICIOUS_STATE_CONTENT = {
  [SUSPICIOUS_STATES.FINGERPRINT_FAILED]: {
    title: `${TP}.FN_TRUST_INFO_FINGERPRINT_FAILED_TITLE`,
    description: `${TP}.FN_TRUST_INFO_FINGERPRINT_FAILED_DESC`,
  },
  [SUSPICIOUS_STATES.UNKNOWN_USER]: {
    title: `${TP}.FN_TRUST_INFO_UNKNOWN_USER_TITLE`,
    description: `${TP}.FN_TRUST_INFO_UNKNOWN_USER_DESC`,
  },
  [SUSPICIOUS_STATES.UNKNOWN_BOT]: {
    title: `${TP}.FN_TRUST_INFO_UNKNOWN_BOT_TITLE`,
    description: `${TP}.FN_TRUST_INFO_UNKNOWN_BOT_DESC`,
  },
  [SUSPICIOUS_STATES.SUSPICIOUS_USER]: {
    title: `${TP}.FN_TRUST_INFO_SUSPICIOUS_USER_TITLE`,
    description: `${TP}.FN_TRUST_INFO_SUSPICIOUS_USER_DESC`,
  },
  [SUSPICIOUS_STATES.SUSPICIOUS_BOT]: {
    title: `${TP}.FN_TRUST_INFO_SUSPICIOUS_BOT_TITLE`,
    description: `${TP}.FN_TRUST_INFO_SUSPICIOUS_BOT_DESC`,
  },
  [SUSPICIOUS_STATES.TEMPORARY_ACCESS_EXPIRED]: {
    title: `${TP}.FN_TRUST_INFO_TEMP_ACCESS_EXPIRED_TITLE`,
    description: `${TP}.FN_TRUST_INFO_TEMP_ACCESS_EXPIRED_DESC`,
  },
  [SUSPICIOUS_STATES.ACCOUNT_BLOCKED]: {
    title: `${TP}.FN_TRUST_INFO_ACCOUNT_BLOCKED_TITLE`,
    description: `${TP}.FN_TRUST_INFO_ACCOUNT_BLOCKED_DESC`,
  },
};

const FooterContent = ({ statusIdentifier, trustInfo, visitorInfo, errorId }) => {
  const { isLoggedIn, language } = useAppContext();
  const { t } = useTranslation('NS_APP_GLOBALS');
  const userData = useUserData();
  const [showLogin, setShowLogin] = useState(false);
  const { isInitialized, init, capture } = useMarkerIO();

  useEffect(() => {
    if (!isInitialized) {
      init();
    }
  }, [init, isInitialized]);

  const captureMarkerIO = useCallback(() => {
    if (isInitialized) {
      capture({
        customData: {
          visitorId: visitorInfo?.data?.visitorId,
          requestId: visitorInfo?.data?.requestId,
          userId: userData?.id,
          sentryErrorId: errorId,
          statusIdentifier,
          trustInfo,
          fingerprintError: visitorInfo?.error?.name,
        },
      });
    }
  }, [isInitialized, capture, statusIdentifier, trustInfo, visitorInfo, userData, errorId]);

  if (showLogin) {
    return (
      <div className={classes.footer}>
        <div className={classes.footer__login}>
          <Login isModal={false} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.footer}>
      <div className={classes.footer__content}>
        {statusIdentifier !== SUSPICIOUS_STATES.TEMPORARY_ACCESS_EXPIRED && (
          <CommonTrans
            i18nKey={`${TP}.FN_TRUST_INFO_FOOT_NOTE`}
            ns="NS_APP_GLOBALS"
            components={{
              p: <Typography variant="p" size="14" />,
              ul: <ul className={classes.footer__list} />,
              li: <Typography variant="li" size="14" />,
            }}
          />
        )}
        {isLoggedIn && (
          <Typography variant="p" size="14" weight="bold" align="center">
            {t(`${TP}.YOU_ARE_LOGGEDIN_ALREADY`)} {userData?.email}
          </Typography>
        )}
      </div>
      <div className={classes.footer__actions}>
        {isLoggedIn ? (
          <PrimaryButton
            shape="rectangle"
            size="small"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.href = `/auth/logout?lang=${language}`;
              }
            }}
          >
            {t(`${TP}.m_LOGOUT`)}
          </PrimaryButton>
        ) : (
          <PrimaryButton shape="rectangle" size="small" onClick={() => setShowLogin(true)}>
            {t(`${TP}.m_LOGIN`)}
          </PrimaryButton>
        )}
        {isInitialized && (
          <SecondaryButton shape="rectangle" size="small" onClick={captureMarkerIO}>
            {t(`${TP}.REPORT`)}
          </SecondaryButton>
        )}
      </div>
    </div>
  );
};

const SuspiciousUser = ({
  visitorInfo,
  isVerifying,
  isFingerprintLoading,
  trustInfo,
  hasTemporaryAccess,
  hasTemporaryAccessExpired,
  hasFingerprintFailed,
}) => {
  const [errorId, setErrorId] = useState(null);
  const { isLoggedIn } = useAppContext();
  const { t } = useTranslation('NS_APP_GLOBALS');

  const statusIdentifier = useMemo(() => {
    if (hasFingerprintFailed) {
      return SUSPICIOUS_STATES.FINGERPRINT_FAILED;
    }

    if (isLoggedIn) {
      if (hasTemporaryAccess && hasTemporaryAccessExpired) {
        return SUSPICIOUS_STATES.TEMPORARY_ACCESS_EXPIRED;
      }

      return SUSPICIOUS_STATES.ACCOUNT_BLOCKED;
    }

    const isBot = trustInfo?.visitorType === VISITOR_TYPES.BOT;
    const isBlocked = trustInfo?.status === RENDERING_STATUSES.BLOCK;

    if (isBlocked) {
      if (isBot) {
        return SUSPICIOUS_STATES.SUSPICIOUS_BOT;
      }

      return SUSPICIOUS_STATES.SUSPICIOUS_USER;
    }

    if (isBot) {
      return SUSPICIOUS_STATES.UNKNOWN_BOT;
    }

    return SUSPICIOUS_STATES.UNKNOWN_USER;
  }, [
    isLoggedIn,
    hasTemporaryAccess,
    hasTemporaryAccessExpired,
    hasFingerprintFailed,
    trustInfo?.visitorType,
    trustInfo?.status,
  ]);

  const { title, description } = useMemo(() => {
    const content = SUSPICIOUS_STATE_CONTENT[statusIdentifier] || SUSPICIOUS_STATE_CONTENT.UNKNOWN_USER;

    return {
      title: t(content.title),
      description: t(content.description),
    };
  }, [statusIdentifier, t]);

  const trackSuspiciousActivity = useCallback(() => {
    const eventTitle = `Suspicious Activity - ${statusIdentifier}`;

    // NOTE: Report to Google Analytics
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: EVENT_TYPES.PAGE_VIEW,
        id: eventTitle,
        metaId: statusIdentifier,
        metaCriteria: visitorInfo?.error?.message || visitorInfo?.error?.name,
        status: trustInfo?.status,
        visitorType: trustInfo?.visitorType,
      });
    }

    // NOTE: Report to Sentry
    const sentryErrorId = sentryCaptureMessage(eventTitle, {
      extra: {
        context: {
          ...visitorInfo?.data,
          ...(visitorInfo?.error && {
            fingerprintErrorName: visitorInfo?.error?.name,
            fingerprintErrorMessage: visitorInfo?.error?.message,
            fingerprintErrorStack: visitorInfo?.error?.stack,
          }),
          ...trustInfo,
        },
      },
    });

    // NOTE: Capture sentry error id
    if (sentryErrorId) {
      setErrorId(sentryErrorId);
    }

    blitzerAPI.POST(EVENTS_ENDPOINTS.TRUST_LOGS, {
      trust: trustInfo,
      status: RENDERING_STATUSES.BLOCK,
      reason: statusIdentifier,
    });
  }, [visitorInfo, trustInfo, statusIdentifier]);

  useEffect(() => {
    if (!isVerifying && !isFingerprintLoading) {
      trackSuspiciousActivity();
    }
  }, [trackSuspiciousActivity, isVerifying, isFingerprintLoading]);

  if (isVerifying) {
    return (
      <div className={classes.loader}>
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Head>
        <meta
          name="suspicious-activity"
          content={[
            statusIdentifier,
            trustInfo?.visitorType || '',
            trustInfo?.status || '',
            trustInfo?.source || '',
            trustInfo?.createdAt || '',
            trustInfo?.ipAddress || '',
          ].join('_')}
        />
      </Head>
      <SEOComponent overrideTitle={`${title} - Operabase`} overrideDescription={description} />
      <ErrorContent
        title={title}
        description={description}
        footer={
          <FooterContent
            errorId={errorId}
            trustInfo={trustInfo}
            visitorInfo={visitorInfo}
            statusIdentifier={statusIdentifier}
          />
        }
        errorId={errorId}
      />
    </>
  );
};

export default SuspiciousUser;
